import { format } from 'date-fns';
import {
  KeyRound,
  Pencil,
  Search,
  Shield,
  ShieldOff,
  Upload,
  UserPlus,
  UserX,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ActionDropdown } from '../components/ActionDropdown';
import { Badge } from '../components/Badge';
import { BulkImportModal } from '../components/forms/BulkImportModal';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import { EditUserModal } from '../components/forms/EditUserModal';
import { ForceAssignModal } from '../components/forms/ForceAssignModal';
import {
  type PlatformUser,
  useResetPassword,
  useUpdateUserFull,
  useUsers,
} from '../hooks/useAdminData';
import { useDebounce } from '../hooks/useDebounce';
import { navigate } from '../lib/navigation';

function roleBadgeVariant(role: string): 'default' | 'info' | 'success' | 'primary' | 'warning' {
  switch (role.toLowerCase()) {
    case 'admin':
      return 'primary';
    case 'manager':
      return 'info';
    case 'sales_rep':
      return 'success';
    default:
      return 'default';
  }
}

const PAGE_SIZE = 50;

function UserOrganizations({ user }: { user: PlatformUser }) {
  if (user.organizations.length === 0) {
    return <span className="text-text-muted italic">No organization</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {user.organizations.map((org) => (
        <span
          key={org.id}
          role="link"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/organizations/${org.id}`);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              navigate(`/organizations/${org.id}`);
            }
          }}
          className="px-2 py-0.5 text-xs rounded-md bg-surface-hover text-text-secondary hover:text-primary transition-colors cursor-pointer"
        >
          {org.name}
        </span>
      ))}
    </div>
  );
}

export function Users() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [platformRoleFilter, setPlatformRoleFilter] = useState('');
  const [page, setPage] = useState(1);

  const [editTarget, setEditTarget] = useState<PlatformUser | null>(null);
  const [assignTarget, setAssignTarget] = useState<PlatformUser | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    user: PlatformUser;
    action: 'promote' | 'demote' | 'disable' | 'enable' | 'reset-password';
  } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const debouncedSearch = useDebounce(search, 300);
  const updateUser = useUpdateUserFull();
  const resetPassword = useResetPassword();

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, limit: PAGE_SIZE };
    if (debouncedSearch) p.search = debouncedSearch;
    if (roleFilter) p.role = roleFilter;
    if (platformRoleFilter) p.platformRole = platformRoleFilter;
    return p;
  }, [page, debouncedSearch, roleFilter, platformRoleFilter]);

  const { data, isLoading } = useUsers(params);

  const users = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  function handleConfirmAction() {
    if (!confirmAction) return;
    const { user, action } = confirmAction;

    if (action === 'promote') {
      updateUser.mutate(
        { userId: user.id, data: { platformRole: 'platform_admin' } },
        {
          onSuccess: () => {
            showNotification('success', 'User promoted to platform admin');
            setConfirmAction(null);
          },
          onError: () => showNotification('error', 'Failed to promote user'),
        },
      );
    } else if (action === 'demote') {
      updateUser.mutate(
        { userId: user.id, data: { platformRole: null } },
        {
          onSuccess: () => {
            showNotification('success', 'Platform admin access removed');
            setConfirmAction(null);
          },
          onError: () => showNotification('error', 'Failed to demote user'),
        },
      );
    } else if (action === 'disable' || action === 'enable') {
      updateUser.mutate(
        { userId: user.id, data: { isActive: action === 'enable' } },
        {
          onSuccess: () => {
            showNotification('success', action === 'enable' ? 'User enabled' : 'User disabled');
            setConfirmAction(null);
          },
          onError: () => showNotification('error', `Failed to ${action} user`),
        },
      );
    } else if (action === 'reset-password') {
      resetPassword.mutate(user.id, {
        onSuccess: () => {
          showNotification('success', 'Password reset email sent');
          setConfirmAction(null);
        },
        onError: () => showNotification('error', 'Failed to reset password'),
      });
    }
  }

  function buildActions(row: PlatformUser) {
    const isPlatformAdmin = row.platformRole === 'platform_admin';
    return [
      { label: 'Edit', icon: Pencil, onClick: () => setEditTarget(row) },
      isPlatformAdmin
        ? {
            label: 'Demote from Admin',
            icon: ShieldOff,
            onClick: () => setConfirmAction({ user: row, action: 'demote' as const }),
          }
        : {
            label: 'Promote to Admin',
            icon: Shield,
            onClick: () => setConfirmAction({ user: row, action: 'promote' as const }),
          },
      {
        label: 'Assign to Org',
        icon: UserPlus,
        onClick: () => setAssignTarget(row),
      },
      {
        label: 'Reset Password',
        icon: KeyRound,
        onClick: () => setConfirmAction({ user: row, action: 'reset-password' as const }),
      },
      {
        label: 'Disable',
        icon: UserX,
        variant: 'danger' as const,
        onClick: () => setConfirmAction({ user: row, action: 'disable' as const }),
      },
    ];
  }

  const confirmMessages: Record<
    string,
    { title: string; message: string; label: string; variant: 'primary' | 'danger' }
  > = {
    promote: {
      title: 'Promote to Platform Admin',
      message: `Grant platform admin access to ${confirmAction?.user.fullName}?`,
      label: 'Promote',
      variant: 'primary',
    },
    demote: {
      title: 'Remove Platform Admin',
      message: `Remove platform admin access from ${confirmAction?.user.fullName}?`,
      label: 'Remove',
      variant: 'danger',
    },
    disable: {
      title: 'Disable User',
      message: `Disable ${confirmAction?.user.fullName}? They will not be able to log in.`,
      label: 'Disable',
      variant: 'danger',
    },
    enable: {
      title: 'Enable User',
      message: `Re-enable ${confirmAction?.user.fullName}?`,
      label: 'Enable',
      variant: 'primary',
    },
    'reset-password': {
      title: 'Reset Password',
      message: `Send a password reset email to ${confirmAction?.user.fullName}?`,
      label: 'Reset Password',
      variant: 'primary',
    },
  };

  const confirmMsg = confirmAction ? confirmMessages[confirmAction.action] : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Platform-wide users. Manage org membership from each organization's Members tab.
          </p>
        </div>
        <button
          onClick={() => setBulkOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          <Upload size={16} />
          Bulk Import
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="sales_rep">Sales Rep</option>
        </select>

        <select
          value={platformRoleFilter}
          onChange={(e) => {
            setPlatformRoleFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Platform Roles</option>
          <option value="platform_admin">Platform Admin</option>
          <option value="regular">Regular</option>
        </select>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
          <div className="h-4 bg-border rounded w-5/6" />
        </div>
      ) : users.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-text-muted text-sm">
          No users found
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-5 py-2.5 font-medium text-xs">Name</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Email</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Role</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Platform Role</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Organizations</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Last Active</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Created</th>
                  <th className="px-5 py-2.5 font-medium text-xs" />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors cursor-pointer"
                    onClick={() => navigate(`/users/${user.id}`)}
                  >
                    <td className="px-5 py-2.5">
                      <span className="text-text font-medium">{user.fullName}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">{user.email}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <Badge variant={roleBadgeVariant(user.role)}>{user.role}</Badge>
                    </td>
                    <td className="px-5 py-2.5">
                      {user.platformRole === 'platform_admin' ? (
                        <Badge variant="primary">Platform Admin</Badge>
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <UserOrganizations user={user} />
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {user.lastActiveAt
                          ? format(new Date(user.lastActiveAt), 'MMM d, yyyy')
                          : 'Never'}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {format(new Date(user.createdAt), 'MMM d, yyyy')}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <div
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <ActionDropdown actions={buildActions(user)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-sm text-text-secondary border-t border-border">
            <span>
              {(data?.total ?? 0) > 0
                ? `${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, data?.total ?? 0)} of ${data?.total}`
                : '0 results'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <EditUserModal open={!!editTarget} onClose={() => setEditTarget(null)} user={editTarget} />

      {assignTarget && (
        <ForceAssignModal
          open
          onClose={() => setAssignTarget(null)}
          userId={assignTarget.id}
          userName={assignTarget.fullName}
        />
      )}

      {confirmMsg && (
        <ConfirmDialog
          open={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirmAction}
          title={confirmMsg.title}
          message={confirmMsg.message}
          confirmLabel={confirmMsg.label}
          confirmVariant={confirmMsg.variant}
          loading={updateUser.isPending || resetPassword.isPending}
        />
      )}

      <BulkImportModal open={bulkOpen} onClose={() => setBulkOpen(false)} />

      {notification && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-sm shadow-lg z-50 ${
            notification.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}
